import React, { useState, useEffect } from "react";
import Modal from "@/components/ui/modal";
import { Badge } from "@/components/ui/badge";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import {
  Wrench,
  Printer,
  Edit,
  User,
  Briefcase,
  Monitor,
  AlertCircle,
  Calculator,
  Tag,
  Clock,
  Package,
  MessageSquare,
  Calendar,
  FileText,
  Eye,
  EyeOff,
  Loader2,
  CreditCard,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// ─── helpers ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  completed: { variant: "success" },
  delivered: { variant: "success" },
  ready: { variant: "success" },
  ready_for_pickup: { variant: "success" },
  in_progress: { variant: "info" },
  diagnosing: { variant: "info" },
  testing: { variant: "info" },
  awaiting_parts: { variant: "warning" },
  on_hold: { variant: "warning" },
  pending: { variant: "warning" },
  cancelled: { variant: "danger" },
  rejected: { variant: "danger" },
};

const PRIORITY_CONFIG = {
  urgent: { variant: "danger" },
  critical: { variant: "danger" },
  high: { variant: "warning" },
  normal: { variant: "info" },
  medium: { variant: "info" },
  low: { variant: "success" },
};

const PAYMENT_STATUS_CONFIG = {
  completed: { variant: "success", label: "Paid" },
  paid: { variant: "success", label: "Paid" },
  "partially-paid": { variant: "warning", label: "Partially Paid" },
  pending: { variant: "warning", label: "Pending" },
  "on-hold": { variant: "warning", label: "On Hold" },
  failed: { variant: "danger", label: "Failed" },
  cancelled: { variant: "danger", label: "Cancelled" },
  refunded: { variant: "secondary", label: "Refunded" },
  "partially-refunded": { variant: "secondary", label: "Partially Refunded" },
};

const fmt = (val) => val ?? "-";
const humanize = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-";
const fmtDate = (val) =>
  val
    ? new Date(val).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "-";
const fmtMoney = (val, sym = "") =>
  `${sym}${parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-muted-foreground uppercase font-bold mb-0.5">
      {label}
    </p>
    <p className="text-sm font-medium">{value ?? "-"}</p>
  </div>
);

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} className="text-primary" />
    <h6 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
      {title}
    </h6>
  </div>
);

// ─── component ───────────────────────────────────────────────────────────────
const RepairDetailsModal = ({
  show,
  onHide,
  viewRepairData,
  currencySymbol,
  handleEdit,
  onPrintPDF,
  onRecordPayment,
  hasPermission = () => true,
}) => {
  const [revealedPassword, setRevealedPassword] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);

  useEffect(() => {
    if (!show) {
      setRevealedPassword(null);
      setPasswordVisible(false);
    }
  }, [show]);

  const handleRevealPassword = async () => {
    if (revealedPassword !== null) {
      setPasswordVisible((v) => !v);
      return;
    }
    setIsRevealing(true);
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.REPAIRS.DEVICE_PASSWORD(viewRepairData.id),
      );
      setRevealedPassword(
        res.data?.device_password ?? res.data?.password ?? "",
      );
      setPasswordVisible(true);
    } catch {
      setRevealedPassword(null);
    } finally {
      setIsRevealing(false);
    }
  };

  if (!viewRepairData) return null;

  const d = viewRepairData;

  // Customer name - handle business vs individual
  const customerName =
    d.customer?.customer_type !== "individual" && d.customer?.company
      ? d.customer.company
      : d.customer?.full_name || "-";

  // Categories - API returns repair_categories array via `with: ["repairCategories"]`
  const categories = d.repair_categories || [];

  // Status / priority
  const statusVal =
    typeof d.status === "string"
      ? d.status
      : d.status?.value || d.status?.label || "";
  const priorityVal =
    typeof d.priority === "string"
      ? d.priority
      : d.priority?.value || d.priority?.label || "";

  const statusCfg = STATUS_CONFIG[statusVal?.toLowerCase()] || {
    variant: "secondary",
  };
  const priorityCfg = PRIORITY_CONFIG[priorityVal?.toLowerCase()] || {
    variant: "secondary",
  };

  // Parts, Labor & Status History
  const parts = d.parts || [];
  const laborEntries = d.labor_entries || d.laborEntries || [];
  const statusHistory = d.status_history || d.statusHistory || [];

  // Payments
  const payments = d.payments || [];
  const totalPaid =
    d.paid_amount != null
      ? parseFloat(d.paid_amount)
      : payments
        .filter((p) => {
          const s = (p.payment_status || "completed").toLowerCase();
          return s === "completed" || s === "paid" || s === "partially-paid";
        })
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalCostVal = parseFloat(d.total_cost ?? d.estimated_cost ?? 0);
  const remainingBalance =
    d.balance_due != null
      ? parseFloat(d.balance_due)
      : Math.max(0, parseFloat((totalCostVal - totalPaid).toFixed(2)));

  const pStatus = (d.payment_status || "").toLowerCase();
  const isPaymentCompleted =
    pStatus === "completed" ||
    pStatus === "paid" ||
    (remainingBalance <= 0 && (totalPaid > 0 || totalCostVal > 0));

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title={
        <div className="flex flex-col">
          <span>Repair Job Details</span>
          <span className="text-xs font-normal text-muted-foreground uppercase tracking-widest">
            #{d.repair_number}
          </span>
        </div>
      }
      icon={Wrench}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex gap-2 justify-end w-full">
          {hasPermission("repair job read") && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                onHide();
                if (onPrintPDF) onPrintPDF(d);
              }}
            >
              <Printer size={16} /> Print
            </Button>
          )}
          {hasPermission("repair job update") && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => handleEdit(d)}
            >
              <Edit size={16} /> Edit Repair
            </Button>
          )}
          {hasPermission("repair job add_payment") &&
            onRecordPayment &&
            !isPaymentCompleted && (
              <Button
                className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => {
                  onHide();
                  onRecordPayment(d);
                }}
              >
                <CreditCard size={16} /> Record Payment
              </Button>
            )}
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-1">
        {/* ── Customer ── */}
        <Card className="bg-muted/30 border-none shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <User size={14} className="text-primary" /> Customer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow
              label="Name"
              value={
                <span className="font-semibold text-base">{customerName}</span>
              }
            />
            <Separator className="bg-muted-foreground/10" />
            <InfoRow label="Email" value={d.customer?.email || "No email"} />
            <InfoRow label="Phone" value={d.customer?.phone || "No phone"} />
            {d.customer?.street_address && (
              <InfoRow
                label="Address"
                value={[
                  d.customer.house_number,
                  d.customer.street_address,
                  d.customer.city,
                  d.customer.state,
                  d.customer.postcode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            )}
          </CardContent>
        </Card>

        {/* ── Service Details ── */}
        <Card className="bg-muted/30 border-none shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Briefcase size={14} className="text-primary" /> Service Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                  Status
                </p>
                <Badge variant={statusCfg.variant}>
                  {humanize(statusVal) || "-"}
                </Badge>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                  Priority
                </p>
                <Badge variant={priorityCfg.variant}>
                  {humanize(priorityVal) || "-"}
                </Badge>
              </div>
              {hasPermission("repair job view_payments") && d.payment_status && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                    Payment
                  </p>
                  <Badge
                    variant={
                      PAYMENT_STATUS_CONFIG[d.payment_status?.toLowerCase()]?.variant || "secondary"
                    }
                  >
                    {PAYMENT_STATUS_CONFIG[d.payment_status?.toLowerCase()]?.label ||
                      humanize(d.payment_status)}
                  </Badge>
                </div>
              )}
            </div>
            <Separator className="bg-muted-foreground/10" />
            <InfoRow
              label="Technician"
              value={d.technician?.full_name || "Unassigned"}
            />
            <InfoRow label="Store" value={d.store?.name || "-"} />
            <InfoRow label="Created" value={fmtDate(d.created_at)} />
            {d.estimated_completion_date && (
              <InfoRow
                label="Est. Completion"
                value={fmt(d.estimated_completion_date)}
              />
            )}
          </CardContent>
        </Card>

        {/* ── Device Information ── */}
        <div className="md:col-span-2 space-y-3">
          <SectionHeader icon={Monitor} title="Device Information" />
          <Card className="overflow-hidden border-muted/30">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0">
              <div className="p-4 space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                  Description
                </span>
                <span className="text-sm font-medium">
                  {fmt(d.item_description)}
                </span>
              </div>
              <div className="p-4 space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                  Categories
                </span>
                {categories.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {categories.map((cat) => (
                      <Badge
                        key={cat.id}
                        variant="secondary"
                        className="text-xs"
                      >
                        {cat.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </div>
              <div className="p-4 space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                  Serial / IMEI
                </span>
                <span className="text-sm font-mono">
                  {fmt(d.serial_number)}
                </span>
              </div>
              <div className="p-4 space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-bold block">
                  Device Password
                </span>
                {hasPermission("repair job view_device_password") ? (
                  d.device_password ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono">
                        {revealedPassword !== null && passwordVisible
                          ? revealedPassword || "-"
                          : "••••••"}
                      </span>
                      <button
                        type="button"
                        onClick={handleRevealPassword}
                        disabled={isRevealing}
                        className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        title={
                          passwordVisible ? "Hide password" : "Reveal password"
                        }
                      >
                        {isRevealing ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : passwordVisible ? (
                          <EyeOff size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )
                ) : (
                  <span className="text-sm text-muted-foreground">***</span>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* ── Reported Issue ── */}
        <div className="space-y-3">
          <SectionHeader icon={AlertCircle} title="Reported Issue" />
          <div className="p-4 bg-muted/20 rounded-lg border border-dashed border-muted-foreground/20 text-sm leading-relaxed min-h-[90px]">
            {d.issue_description || "No description provided."}
          </div>
        </div>

        {/* ── Diagnosis ── */}
        <div className="space-y-3">
          <SectionHeader icon={Wrench} title="Technical Diagnosis" />
          <div className="p-4 bg-muted/20 rounded-lg border border-dashed border-muted-foreground/20 text-sm leading-relaxed min-h-[90px] italic">
            {d.diagnosis || "No diagnosis recorded."}
          </div>
        </div>

        {/* ── Notes ── */}
        {(d.customer_notes || d.technician_notes) && (
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {d.customer_notes && (
              <div className="space-y-2">
                <SectionHeader icon={MessageSquare} title="Customer Notes" />
                <div className="p-3 bg-muted/20 rounded-lg border border-dashed border-muted-foreground/20 text-sm leading-relaxed">
                  {d.customer_notes}
                </div>
              </div>
            )}
            {d.technician_notes && (
              <div className="space-y-2">
                <SectionHeader icon={FileText} title="Technician Notes" />
                <div className="p-3 bg-muted/20 rounded-lg border border-dashed border-muted-foreground/20 text-sm leading-relaxed">
                  {d.technician_notes}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Parts ── */}
        {parts.length > 0 && (
          <div className="md:col-span-2 space-y-3">
            <SectionHeader
              icon={Package}
              title={`Parts & Components (${parts.length})`}
            />
            <Card className="overflow-hidden border-muted/30">
              <div className="grid grid-cols-4 px-4 py-2 bg-muted/40 text-[10px] uppercase font-bold text-muted-foreground border-b border-border">
                <span className="col-span-2">Part / Product</span>
                <span className="text-center">Qty × Unit Cost</span>
                <span className="text-right">Total</span>
              </div>
              <div className="divide-y divide-border">
                {parts.map((part, i) => (
                  <div
                    key={part.id || i}
                    className="grid grid-cols-4 px-4 py-2.5 text-sm items-center"
                  >
                    <span className="font-medium col-span-2 truncate">
                      {part.product?.name || `Part #${i + 1}`}
                      {(() => {
                        let serials = [];
                        if (Array.isArray(part.serial_numbers)) {
                          serials = part.serial_numbers;
                        } else if (
                          typeof part.serial_numbers === "string" &&
                          part.serial_numbers !== "null" &&
                          part.serial_numbers.trim() !== ""
                        ) {
                          try {
                            serials = JSON.parse(part.serial_numbers);
                          } catch {
                            serials = [part.serial_numbers];
                          }
                        } else if (part.serial_number) {
                          serials = [part.serial_number];
                        }
                        if (serials.length > 0) {
                          return (
                            <span className="block text-[11px] text-muted-foreground font-normal mt-0.5">
                              Serial: {serials.join(", ")}
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {part.notes && (
                        <span className="block text-[11px] text-muted-foreground font-normal">
                          {part.notes}
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground text-center">
                      {parseFloat(part.quantity_used ?? 1).toFixed(2)} ×{" "}
                      {fmtMoney(part.unit_cost, currencySymbol)}
                    </span>
                    <span className="text-right font-medium">
                      {fmtMoney(part.total_cost, currencySymbol)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Labor ── */}
        {laborEntries.length > 0 && (
          <div className="md:col-span-2 space-y-3">
            <SectionHeader
              icon={Clock}
              title={`Labor Entries (${laborEntries.length})`}
            />
            <Card className="overflow-hidden border-muted/30">
              <div className="grid grid-cols-4 px-4 py-2 bg-muted/40 text-[10px] uppercase font-bold text-muted-foreground border-b border-border">
                <span className="col-span-2">Technician</span>
                <span className="text-center">Duration × Rate</span>
                <span className="text-right">Total</span>
              </div>
              <div className="divide-y divide-border">
                {laborEntries.map((entry, i) => (
                  <div
                    key={entry.id || i}
                    className="grid grid-cols-4 px-4 py-2.5 text-sm items-center"
                  >
                    <span className="font-medium col-span-2 truncate">
                      {entry.technician?.full_name || `Technician #${i + 1}`}
                      {entry.description && (
                        <span className="block text-[11px] text-muted-foreground font-normal">
                          {entry.description}
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground text-center">
                      {entry.formatted_duration ||
                        `${entry.duration_minutes ?? 0}m`}
                      {" @ "}
                      {fmtMoney(entry.hourly_rate, currencySymbol)}/hr
                    </span>
                    <span className="text-right font-medium">
                      {fmtMoney(entry.total_cost, currencySymbol)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Status History ── */}
        {statusHistory.length > 0 && (
          <div className="md:col-span-2 space-y-3">
            <SectionHeader
              icon={Clock}
              title={`Status History (${statusHistory.length})`}
            />
            <Card className="overflow-hidden border-muted/30">
              <div className="divide-y divide-border">
                {statusHistory.map((entry, i) => (
                  <div
                    key={entry.id || i}
                    className="p-3.5 text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="capitalize font-semibold">
                        {(entry.status || "N/A").replace(/_/g, " ")}
                      </Badge>
                      {entry.notes && (
                        <span className="text-muted-foreground italic text-xs">
                          &ldquo;{entry.notes}&rdquo;
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                      <span>
                        By:{" "}
                        <strong className="text-foreground font-medium">
                          {entry.changedBy?.full_name ||
                            entry.changedBy?.name ||
                            entry.changed_by?.full_name ||
                            entry.changed_by?.name ||
                            "System"}
                        </strong>
                      </span>
                      <span>•</span>
                      <span>{entry.created_at ? fmtDate(entry.created_at) : "-"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Payments ── */}
        {hasPermission("repair job view_payments") && payments.length > 0 && (
          <div className="md:col-span-2 space-y-3">
            <SectionHeader
              icon={CreditCard}
              title={`Payments Recorded (${payments.length})`}
            />
            <Card className="overflow-hidden border-muted/30">
              <div className="grid grid-cols-4 px-4 py-2 bg-muted/40 text-[10px] uppercase font-bold text-muted-foreground border-b border-border">
                <span className="col-span-2">Method & Details</span>
                <span className="text-center">Date & Status</span>
                <span className="text-right">Amount</span>
              </div>
              <div className="divide-y divide-border">
                {payments.map((p, i) => (
                  <div
                    key={p.id || i}
                    className="grid grid-cols-4 px-4 py-2.5 text-sm items-center"
                  >
                    <span className="font-medium col-span-2 truncate">
                      {typeof p.payment_method === "object" && p.payment_method !== null
                        ? p.payment_method.name || p.payment_method.code || `Payment #${i + 1}`
                        : p.payment_method || `Payment #${i + 1}`}
                      {p.transaction_id && (
                        <span className="block text-[11px] text-muted-foreground font-normal">
                          Ref: {p.transaction_id}
                        </span>
                      )}
                      {p.card_last_four && (
                        <span className="block text-[11px] text-muted-foreground font-normal">
                          Card ending {p.card_last_four}
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground text-center text-xs">
                      {p.payment_date ? fmtDate(p.payment_date) : "-"}
                      <span className="block capitalize font-medium text-[11px] text-foreground">
                        {p.payment_status || "Completed"}
                      </span>
                    </span>
                    <span className="text-right font-medium text-emerald-600">
                      {fmtMoney(p.amount, currencySymbol)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Financial Summary ── */}
        <div className="md:col-span-2 space-y-3">
          <SectionHeader icon={Calculator} title="Financial Summary" />
          <Card className="border-muted/30 overflow-hidden">
            <div className="divide-y divide-border">
              {d.parts_cost != null && parseFloat(d.parts_cost) > 0 && (
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Package size={13} /> Parts Cost
                  </span>
                  <span className="font-medium">
                    {fmtMoney(d.parts_cost, currencySymbol)}
                  </span>
                </div>
              )}
              {d.labor_cost != null && parseFloat(d.labor_cost) > 0 && (
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Clock size={13} /> Labor Cost
                  </span>
                  <span className="font-medium">
                    {fmtMoney(d.labor_cost, currencySymbol)}
                  </span>
                </div>
              )}
              <div className="flex justify-between px-4 py-3 text-sm">
                <span className="text-muted-foreground">Estimated Cost</span>
                <span className="font-medium">
                  {fmtMoney(d.estimated_cost, currencySymbol)}
                </span>
              </div>
              <div className="flex justify-between px-4 py-3 bg-muted/30">
                <span className="font-bold text-base">Total Cost</span>
                <span className="font-bold text-base text-green-600">
                  {fmtMoney(d.total_cost, currencySymbol)}
                </span>
              </div>
              {hasPermission("repair job view_payments") && (
                <>
                  {d.payment_status && (
                    <div className="flex justify-between px-4 py-3 text-sm">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <CreditCard size={13} className="text-primary" /> Payment Status
                      </span>
                      <Badge
                        variant={
                          PAYMENT_STATUS_CONFIG[d.payment_status?.toLowerCase()]?.variant || "secondary"
                        }
                        className="text-xs"
                      >
                        {PAYMENT_STATUS_CONFIG[d.payment_status?.toLowerCase()]?.label ||
                          humanize(d.payment_status)}
                      </Badge>
                    </div>
                  )}
                  {totalPaid > 0 && (
                    <div className="flex justify-between px-4 py-3 text-sm">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <CreditCard size={13} className="text-emerald-600" /> Amount Paid
                      </span>
                      <span className="font-semibold text-emerald-600">
                        {fmtMoney(totalPaid, currencySymbol)}
                      </span>
                    </div>
                  )}
                  {(d.balance_due != null || (totalCostVal > 0 && totalPaid > 0)) && (
                    <div className="flex justify-between px-4 py-3 text-sm bg-muted/10">
                      <span className="text-muted-foreground font-medium">Balance Due</span>
                      <span
                        className={`font-bold ${remainingBalance > 0 ? "text-amber-600" : "text-emerald-600"
                          }`}
                      >
                        {fmtMoney(remainingBalance, currencySymbol)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Modal>
  );
};

RepairDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  viewRepairData: PropTypes.object,
  currencySymbol: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired,
  onPrintPDF: PropTypes.func,
  onRecordPayment: PropTypes.func,
  hasPermission: PropTypes.func,
};

export default RepairDetailsModal;
